import React, { useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartPlus, faPlus } from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/patient-page-modules/PatientDevices.module.css";
import AddDeviceModal from "../../components/patient-detail/patient-modals/addDeviceModal";
import OrderDeviceModal from "../../components/patient-detail/patient-modals/orderDeviceModal";
import RequestDetailModal from "../../components/patient-detail/patient-modals/requestDetailModal";
import TechnicalSupportModal from "../../components/patient-detail/patient-modals/technical-support-request-modal";
import Button from "../../components/button-v1";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAssignedDevices,
  unlinkDevice,
  getOrderedDevices,
  getDropshipedDevices,
  getTickets,
  getTicketEvents,
} from "../../services/patient/devices";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
  DialogDescription,
} from "../../components/ui/dialog";
import { formatDate } from "../../utils/helper";
import { parseDateSafe } from "../../utils/dateFormatters";
import Spinner from "../../components/spinner";

const PatientDevices = ({ currentPatient }) => {
  const patientId = sessionStorage.getItem("_pt_id");
  const queryClient = useQueryClient();
  const [isAddDeviceModalOpen, setIsAddDeviceModalOpen] = useState(false);
  const [isOrderDeviceModalOpen, setIsOrderDeviceModalOpen] = useState(false);
  const [isRequestDetailModalOpen, setIsRequestDetailModalOpen] =
    useState(false);
  const [
    isTechnicalSupportRequestModalOpen,
    setIsTechnicalSupportRequestModalOpen,
  ] = useState(false);

  const [requestData, setRequestData] = useState({});
  const [showUnlinkAlert, setShowUnlinkAlert] = useState(false);
  const [deviceId, setDeviceId] = useState(null);
  const [unlinkReason, setUnlinkReason] = useState("");
  const [ticketId, setTicketId] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const { data: assignedDevices, isLoading: isAssignedDevicesLoading } =
    useQuery(["patient-assigned-devices", patientId], () =>
      getAssignedDevices()
    );

  const { mutate: unlinkDeviceMutation, isLoading: isUnlinkingDevice } =
    useMutation((data) => unlinkDevice(data), {
      onSuccess: () => {
        toast.success("Device unlinked successfully");
        queryClient.invalidateQueries(["patient-assigned-devices"]);
        setShowUnlinkAlert(false);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || "Could not unlink device");
      },
    });

  const { data: orderedDevices, isLoading: isOrderedDevicesLoading } = useQuery(
    ["patient-ordered-devices", patientId],
    () => getOrderedDevices()
  );

  const dropshidDevicesQuery = useQuery(
    ["patient-dropshied-ordered-devices", patientId],
    () => getDropshipedDevices({ patientId })
  );

  const { data: tickets, isLoading: isTicketsLoading } = useQuery(
    ["patient-technical-support-tickets", patientId],
    () => getTickets()
  );

  const ticketEventQuery = useQuery(
    ["technical-support-ticket-events", ticketId],
    () => getTicketEvents(ticketId?.id),
    {
      enabled: !!ticketId,
      onSuccess: () => {
        setIsRequestDetailModalOpen(true);
      },
      onError: (error) => {
        toast.error(error.response.data?.error || "Something went wrong.");
      },
    }
  );

  // add device modal functions
  const openAddDeviceModal = () => {
    setIsAddDeviceModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeAddDeviceModal = useCallback(() => {
    setIsAddDeviceModalOpen(false);
    document.body.style.overflow = "auto";
  }, []);

  // order device modal functions
  const openOrderDeviceModal = () => {
    setIsOrderDeviceModalOpen(true);
    // document.body.style.overflow = "hidden";
  };

  const closeOrderDeviceModal = () => {
    setIsOrderDeviceModalOpen(false);
    // document.body.style.overflow = "auto";
  };

  // Add Request modal functions
  const openRequestDetailModal = (request_data) => {
    setRequestData(request_data);
    setIsRequestDetailModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeRequestDetailModal = () => {
    setIsRequestDetailModalOpen(false);
    document.body.style.overflow = "auto";
  };

  const handleUnlinkDeviceAlert = (deviceId) => {
    setShowUnlinkAlert(true);
    setDeviceId(deviceId);
  };

  const handleUnlinkMutation = () => {
    if (!unlinkReason) return;
    unlinkDeviceMutation({
      device_uuid: deviceId,
      unlinked_reason: unlinkReason,
    });
  };

  const handelLastUpdate = (modified_at) => {
    const parsedDate = parseDateSafe(modified_at);
    if (!parsedDate) return "--";
    const lastUpdate = Math.floor(
      (new Date() - parsedDate) / (1000 * 60 * 60 * 24)
    );
    return lastUpdate === 0 ? "Today" : lastUpdate;
  };

  const handleLogs = (item) => {
    setTicketId(item);
  };

  return (
    <div className={styles.patient_page}>
      <div className={styles.devices_container}>
        {/* Devices Table */}
        <div className={styles.assigned_devices}>
          <div className={styles.header}>
            <h2>Devices</h2>

            <Button variant="ghost" onClick={openAddDeviceModal}>
              <img
                src="/icons/plus.svg"
                alt="plus-icon"
                style={{ width: "14px", height: "14px" }}
              />
              Assign New Device
            </Button>
            {isAddDeviceModalOpen && (
              <AddDeviceModal
                isOpen={isAddDeviceModalOpen}
                onClose={closeAddDeviceModal}
                selectedDevice={selectedDevice}
              />
            )}
          </div>

          <div className={styles.table_container}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>Serial/IMEI</th>
                  <th className={styles.tableHeader}>Type</th>
                  <th className={styles.tableHeader}>Assigned At</th>
                  <th className={styles.tableHeader}>Last Signal Strength</th>
                  <th className={styles.tableHeader}>Status</th>
                  <th className={styles.tableHeader}>HWI Id</th>
                  <th className={styles.tableHeader}>Unlink Device</th>
                </tr>
              </thead>
              <tbody>
                {isAssignedDevicesLoading && (
                  <tr>
                    <td colSpan="7">
                      <Spinner />
                    </td>
                  </tr>
                )}
                {assignedDevices?.map((item, index) => (
                  <tr key={index}>
                    <td className={styles.tableCell}>{item?.hardware_uuid}</td>
                    <td className={styles.tableCell}>{item?.name}</td>
                    <td className={styles.tableCell}>
                      {formatDate(item?.assigned_at)}
                    </td>
                    <td className={styles.tableCell}>
                      {item?.last_signal_strength ?? "_ _"}
                    </td>
                    <td
                      className={`${styles.tableCell} ${styles.status_cell} ${
                        item.status === "Connected"
                          ? styles.option_1
                          : styles.option_2
                      }`}
                    >
                      <div className={styles.status}>
                        <div className={styles.indicator}></div>
                        <p>{item?.status}</p>
                        <div className={styles?.info}>?</div>
                      </div>
                    </td>
                    <td className={styles.tableCell}>{item?.id}</td>
                    <td className={styles.tableCell}>
                      <section className={styles.device_actions}>
                        <Button
                          variant="ghost"
                          size="small"
                          // className={styles.unlink_btn}
                          onClick={() => handleUnlinkDeviceAlert(item?.id)}
                        >
                          Unlink
                        </Button>
                        {/* <Button
                          variant="ghost"
                          size="small"
                          // className={styles.unlink_btn}
                          onClick={() => {
                            openAddDeviceModal();
                            setSelectedDevice(item);
                          }}
                        >
                          Edit
                        </Button> */}
                      </section>
                    </td>
                  </tr>
                ))}
                {assignedDevices?.length <= 0 && (
                  <tr>
                    <td colSpan="7">No Assigned Devices</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Device Order Table */}
        <div className={styles.assigned_devices}>
          <div className={styles.header}>
            <h2>Orders</h2>

            <Button variant="ghost" onClick={openOrderDeviceModal}>
              <FontAwesomeIcon icon={faCartPlus} />
              Order New Device
            </Button>
            {isOrderDeviceModalOpen && (
              <OrderDeviceModal
                isOpen={isOrderDeviceModalOpen}
                onClose={() => setIsOrderDeviceModalOpen(false)}
                currentPatient={currentPatient}
              />
            )}
          </div>

          <div className={styles.table_container}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>Order Date</th>
                  <th className={styles.tableHeader}>Order Id</th>
                  <th className={styles.tableHeader}>Equipment</th>
                  {/* <th className={styles.tableHeader}>Quantity</th> */}
                  <th className={styles.tableHeader}>HWI ID</th>
                  <th className={styles.tableHeader}>Status</th>
                </tr>
              </thead>
              <tbody>
                {/* Ordered Devices goes here */}
                {dropshidDevicesQuery?.isLoading && (
                  <tr>
                    <td colSpan="7">
                      <Spinner />
                    </td>
                  </tr>
                )}
                {dropshidDevicesQuery?.data?.orders?.map((item, index) => (
                  <tr key={index}>
                    <td className={styles.tableCell}>
                      {formatDate(item?.created_at)}
                    </td>
                    <td className={styles.tableCell}>{item?.order_number}</td>
                    <td className={styles.tableCell}>{item?.name}</td>
                    {/* <td className={styles.tableCell}>{item?.quantity}</td> */}
                    <td className={styles.tableCell}>
                      {item?.tenovi_order_id ?? "_"}
                    </td>
                    <td
                      className={`${styles.tableCell} ${styles.status_cell} ${
                        item?.status === "Completed"
                          ? styles.option_1
                          : styles.option_2
                      }`}
                    >
                      <div className={styles.status}>
                        <div className={styles.indicator}></div>
                        <span>{item?.status}</span>
                        <div className={styles.info}>?</div>
                      </div>
                    </td>
                  </tr>
                ))}

                {dropshidDevicesQuery?.data?.orders?.length <= 0 && (
                  <tr>
                    <td colSpan="7">No Ordered Devices</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Technical Support Table */}
        <div className={styles.assigned_devices}>
          <div className={styles.header}>
            <h2>Technical Support</h2>

            <Button
              variant="ghost"
              onClick={() => setIsTechnicalSupportRequestModalOpen(true)}
            >
              <img
                src="/icons/plus.svg"
                alt="plus-icon"
                style={{ width: "14px", height: "14px" }}
              />
              Add request
            </Button>

            {isTechnicalSupportRequestModalOpen && (
              <TechnicalSupportModal
                isOpen={isTechnicalSupportRequestModalOpen}
                onClose={() => setIsTechnicalSupportRequestModalOpen(false)}
              />
            )}
          </div>

          <div className={styles.table_container}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>Sr No</th>
                  <th className={styles.tableHeader}>Device Type</th>
                  <th className={styles.tableHeader}>Serial/IMEI</th>
                  <th className={styles.tableHeader}>Description</th>
                  <th className={styles.tableHeader}>Created On</th>
                  <th className={styles.tableHeader}>Days Since Last Update</th>
                  <th className={styles.tableHeader}>Events</th>
                </tr>
              </thead>
              <tbody>
                {isTicketsLoading && (
                  <tr>
                    <td colSpan="7">
                      <Spinner />
                    </td>
                  </tr>
                )}
                {tickets?.map((item, index) => (
                  <tr
                    key={index}
                    className={styles.technical_request_row}
                    // onClick={() => openRequestDetailModal(item)}
                  >
                    <td className={styles.tableCell}>{item.id}</td>
                    <td className={styles.tableCell}>
                      {
                        orderedDevices?.find(
                          (device) => device.id === item.ordered_device
                        )?.name
                      }

                      {
                        assignedDevices?.find(
                          (device) => device.id === item.assigned_device
                        )?.name
                      }
                      {/* {item.name} */}
                    </td>
                    <td className={styles.tableCell}>
                      {/* {item.hardware_uuid} */}
                      {
                        orderedDevices?.find(
                          (device) => device.id === item.ordered_device
                        )?.hardware_uuid
                      }

                      {
                        assignedDevices?.find(
                          (device) => device.id === item.assigned_device
                        )?.hardware_uuid
                      }
                    </td>
                    <td className={styles.tableCell}>
                      {item.issue_description}
                    </td>
                    <td className={styles.tableCell}>
                      {formatDate(item.created_at)}
                    </td>
                    <td className={styles.tableCell}>
                      {handelLastUpdate(item.modified_at)}
                    </td>
                    <td className={styles.tableCell}>
                      <Button
                        loading={
                          ticketEventQuery.isLoading && ticketId?.id == item?.id
                        }
                        disabled={
                          ticketEventQuery.isLoading && ticketId?.id == item?.id
                        }
                        variant="ghost"
                        size="small"
                        onClick={() => handleLogs(item)}
                      >
                        events
                      </Button>
                    </td>
                  </tr>
                ))}
                {tickets?.length <= 0 && (
                  <tr>
                    <td colSpan="7">No Tickets</td>
                  </tr>
                )}
              </tbody>
            </table>
            {isRequestDetailModalOpen && (
              <RequestDetailModal
                isOpen={isRequestDetailModalOpen}
                onClose={closeRequestDetailModal}
                ticket={ticketId}
              />
            )}
          </div>
        </div>
      </div>

      {/* Unlink Confirmation */}

      <Dialog open={showUnlinkAlert} setOpen={setShowUnlinkAlert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlink Device</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className={styles.input_wrapper}>
              <p>Are you sure you want to unlink the device?</p>
              <label>Reason</label>
              <textarea
                type="text"
                name="reason"
                value={unlinkReason}
                onChange={(e) => setUnlinkReason(e.target.value)}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="normal"
              size="default"
              onClick={() => setShowUnlinkAlert(false)}
            >
              Cancel
            </Button>
            <Button
              loading={isUnlinkingDevice}
              variant="primary"
              size="default"
              onClick={() => handleUnlinkMutation()}
              disabled={!unlinkReason}
            >
              Unlink
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientDevices;
